import React, { useEffect, useState } from "react";
import { Calendar, Loader2, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";
import { useBranding } from "../../lib/branding";

const STATUS_TINT = {
  confirmed: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  completed: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  no_show: "bg-rose-500/10 text-rose-300 border-rose-500/30",
  cancelled: "bg-white/5 text-white/45 border-white/10",
};

const STATUSES = ["confirmed", "completed", "no_show", "cancelled"];

export default function PortalAppointments() {
  const branding = useBranding();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const accent = branding?.accent_color || "#FFB000";

  useEffect(() => {
    setLoading(true);
    const url = filter ? `/portal/appointments?status=${filter}` : "/portal/appointments";
    api.get(url).then(({ data }) => {
      setItems(data.items || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [filter]);

  return (
    <div className="space-y-6" data-testid="portal-appointments-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-medium mb-2" style={{ color: accent }}>Appointments</div>
        <h1 className="text-3xl font-bold tracking-tight">Booked demos</h1>
        <p className="text-sm text-white/50 mt-2">Every appointment your AI front desk has booked.</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="portal-appt-filters">
        <Chip active={!filter} onClick={() => setFilter("")} accent={accent}>All</Chip>
        {STATUSES.map((s) => (
          <Chip key={s} active={filter === s} onClick={() => setFilter(filter === s ? "" : s)} accent={accent}>
            {s.replace("_", " ")}
          </Chip>
        ))}
      </div>

      {loading ? (
        <div className="text-white/50 text-sm flex items-center gap-2 py-10"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-[#121214] border border-white/5 rounded-2xl p-12 text-center" data-testid="portal-appts-empty">
          <Calendar className="w-10 h-10 text-white/20 mx-auto mb-4" strokeWidth={1.5} />
          <div className="text-white/70 font-medium">No appointments yet</div>
          <div className="text-white/40 text-sm mt-1">Bookings will appear here as your AI receptionist locks them in.</div>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((a) => (
            <div key={a.id} className="bg-[#121214] border border-white/5 rounded-xl p-5 flex items-start gap-4" data-testid={`portal-appt-${a.id}`}>
              <div className="w-12 h-12 rounded-lg flex flex-col items-center justify-center flex-shrink-0 border border-white/10" style={{ background: `${accent}15` }}>
                <div className="text-[9px] uppercase tracking-[0.1em]" style={{ color: accent }}>
                  {a.scheduled_at ? new Date(a.scheduled_at).toLocaleString("en-US", { month: "short" }) : "—"}
                </div>
                <div className="text-lg font-bold leading-none" style={{ color: accent }}>
                  {a.scheduled_at ? new Date(a.scheduled_at).getDate() : "—"}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-base font-semibold text-white">{a.name || "—"}</div>
                  <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded border font-medium ${STATUS_TINT[a.status] || STATUS_TINT.confirmed}`}>
                    {(a.status || "confirmed").replace("_", " ")}
                  </span>
                </div>
                <div className="text-[12px] text-white/45 mt-1 flex items-center gap-3 flex-wrap">
                  {a.email && <span>{a.email}</span>}
                  {a.phone && <><span>·</span><span>{a.phone}</span></>}
                  {a.scheduled_at && <><span>·</span><span>{new Date(a.scheduled_at).toLocaleString()}</span></>}
                  {a.source && <><span>·</span><span className="capitalize">{a.source}</span></>}
                </div>
                {a.notes && <div className="text-[12.5px] text-white/65 mt-2 italic">"{a.notes}"</div>}
                {a.calendar_event_link && (
                  <a
                    href={a.calendar_event_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[12px] mt-2 hover:underline"
                    style={{ color: accent }}
                  >
                    Open calendar invite <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Chip({ children, active, onClick, accent }) {
  return (
    <button
      onClick={onClick}
      className={`text-[11px] uppercase tracking-[0.1em] font-medium px-3 py-1.5 rounded-full border transition-colors capitalize ${active ? "" : "text-white/55 border-white/10 hover:text-white"}`}
      style={active ? { background: `${accent}18`, color: accent, borderColor: `${accent}40` } : {}}
    >
      {children}
    </button>
  );
}
